import {
	Component,
	forwardRef,
	Input,
	EventEmitter,
	Output,
} from '@angular/core';
import { CustomerLink, CustomerClient } from 'src/shared/api';
import {
	displayName,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
	takeUntil,
	filter,
	distinctUntilChanged,
	debounceTime,
	map,
	shareReplay,
} from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { TypeAheadService } from 'src/shared/services/type-ahead.service';
import { MatDialog } from '@angular/material/dialog';
import { AddCustomerDialogComponent } from 'src/admin-app/shared/add-customer-dialog/add-customer-dialog.component';
import { customersFragments } from "../../../admin-app/customers/customers.routes";

@Component({
	selector: 'rpshared-customer-link-with-add',
	templateUrl: './customer-link-with-add.component.html',
	styleUrls: ['./customer-link-with-add.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CustomerLinkWithAddComponent),
			multi: true,
		},
	],
})

export class CustomerLinkWithAddComponent extends SelfDestruct
	implements ControlValueAccessor {

	customersFragments = customersFragments;

	private _required = false;
	@Input() isRequest = false;
	
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}
	
	private _multiline = false;
	@Input()
	get multiline(): boolean {
		return this._multiline;
	}
	set multiline(value: boolean) {
		this._multiline = coerceBooleanProperty(value);
	}

	@Output() optionSelected = new EventEmitter<CustomerLink>();

	initialValue: CustomerLink;
	customer = new FormControl('') as FormControl;

	filteredCustomers$ = this.typeAheadService.filteredCustomers$(
		this.customer.valueChanges
	);

	customerAddress$ = this.customer.valueChanges.pipe(
		map((val) => val.address),
		shareReplay(1)
	);

	// Redeclared template variables
	displayName = displayName;

	constructor(
		private readonly typeAheadService: TypeAheadService,
		private readonly customerClient: CustomerClient,
		private readonly dialog: MatDialog
	) {
		super();
	}

	// model --> view
	writeValue(value: CustomerLink | number): void {

		if (typeof value === 'number') {
			this.customerClient.getCustomerLink(value).subscribe((customer) => {
				this.initialValue = customer;
				this.customer.setValue(customer, { emitEvent: false });
			});
		} else if (value) {
			this.initialValue = value;
			this.customer.setValue(value, { emitEvent: false });
		} else {
			this.customer.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: CustomerLink | number) => void) {
		this.customer.valueChanges
			.pipe(
				debounceTime(200),
				filter((v) => !v || typeof v !== 'string'),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.customer)(fn);
	}

	onTouched: () => void = () => {};

	get customerSelected() {
		const customer = this.customer.value as CustomerLink;
		return customer && customer.id;
	}

	addNewCustomer() {
		this.dialog
			.open(AddCustomerDialogComponent, {
				minWidth: '500px',
				data: this.customer.value,
			})
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe((customerLink) => {
				this.customer.setValue(customerLink);
				this.optionSelected.emit(customerLink);
			});
	}
}
